import _ from 'lodash';
import Vue from 'vue';
import {mapActions, mapGetters, mapState} from 'vuex';
import {required} from 'vuelidate/lib/validators';
import {AxiosResponse, AxiosError} from 'axios';
import {ErrorType} from '../../lib/enums';
import {EmployeeProfileModuleConstant, FreeProgramConstant, PageDriverConstant, RegularExpressionConstant, SectionConstant, UrlModuleConstant} from '../../lib/constants';
import {personService} from '../../lib/services/personService';
import {trainerAccessService} from '../../lib/services/trainerAccessService';
import TrainerAccess from './trainerAccess.html';
import EcCommon from '../../../types/ec/ecCommon';
import LtFramework from '../../../types/lt/ltFramework';

interface IComponentData {
	employeeId: string;
	formSubmitted: boolean;
	initializing: boolean;
	employeeSearchResponse: EcCommon.IEmployeeSearchResponse;
	trainerAccessService: EcCommon.ITrainerAccessService;
	isEmailIdAlreadyExist: boolean;
	trainerRoleFormSubmitted: boolean;
	trainerRoleForm: EcCommon.ITrainerRoleForm;
}

export default Vue.component('ecTrainerAccess', {
	template: TrainerAccess,
	data(): IComponentData {
		return {
			employeeId: '',
			formSubmitted: false,
			initializing: true,
			employeeSearchResponse: {} as EcCommon.IEmployeeSearchResponse,
			trainerAccessService,
			isEmailIdAlreadyExist: false,
			trainerRoleFormSubmitted: false,
			trainerRoleForm: {} as EcCommon.ITrainerRoleForm,
		};
	},
	computed: {
		...mapGetters(EmployeeProfileModuleConstant.name, {
			getIsEmployeeProfileValid: EmployeeProfileModuleConstant.getter.getIsValid,
		}),
		...mapGetters(UrlModuleConstant.name, [UrlModuleConstant.getter.getGlobalPageDriverUrl]),
		...mapState(EmployeeProfileModuleConstant.name, [EmployeeProfileModuleConstant.state.employeeProfile]),
		loading(): boolean {
			return this.initializing || this.trainerAccessService.loading;
		},
		errorMessage(): string {
			let errorMessage = '';
			if (this.trainerAccessService.errorMessage) {
				errorMessage = this.trainerAccessService.errorMessage;
			}

			return errorMessage;
		},
	},
	methods: {
		validateUserRole(): Promise<void> {
			return personService.getPersonRoles(FreeProgramConstant.ApplicationId, FreeProgramConstant.RoleId)
				.then((response: AxiosResponse<EcCommon.IEmployeeUser[]>) => {
					const employee = _.find(response.data, ['EmployeeId', (this.employeeProfile.employeeDetails as LtFramework.IEmployeeDetails).employeeId]);
					if (employee) {
						const role = _.find(employee.Roles, (employeeRole) => {
							return employeeRole.ApplicationId.toUpperCase() === FreeProgramConstant.ApplicationId && employeeRole.RoleId.toUpperCase() === FreeProgramConstant.RoleId;
						});
						if (!role) {
							window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.home));
						}

						return Promise.resolve();
					} else {
						window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.home));

						return Promise.resolve();
					}
				});
		},
		searchEmployee(): Promise<void> {
			if (!this.employeeId) {
				return Promise.resolve();
			}

			this.formSubmitted = true;
			this.employeeSearchResponse = {} as EcCommon.IEmployeeSearchResponse;

			return trainerAccessService.searchEmployee(this.employeeId)
				.then((response: AxiosResponse<EcCommon.IEmployeeSearchResponse>) => {
					this.employeeSearchResponse = response.data;
				});
		},
		clearTerm() {
			this.employeeId = '';
			this.$nextTick(() => {
				const el = this.$refs.employeeInput as HTMLElement;
				el.click();
				el.focus();
			});
		},
		resetFormStatus() {
			this.formSubmitted = false;
		},
		resetTrainerRoleFormStatus() {
			this.isEmailIdAlreadyExist = false;
			this.trainerRoleFormSubmitted = false;
			this.trainerRoleForm = {} as EcCommon.ITrainerRoleForm;
		},
		deactivateEmployee(): Promise<void> {
			return trainerAccessService.deactivateEmployee(this.employeeSearchResponse.partyId)
				.then((response: AxiosResponse<void>) => {
					this.$set(this.employeeSearchResponse, 'isActive', false);
					$('#deactivateModal').modal('hide');
				});
		},
		activateEmployee(): Promise<void> {
			this.trainerRoleFormSubmitted = true;
			if (this.$v.$invalid) {
				return Promise.resolve();
			}

			return trainerAccessService.activateEmployee(this.employeeSearchResponse, this.trainerRoleForm.trainerRole)
				.then((response: AxiosResponse<EcCommon.IAddEmployeeResponse>) => {
					this.$set(this.employeeSearchResponse, 'isActive', true);
					this.$set(this.employeeSearchResponse, 'sourceId', response.data.sourceId);
					$('#trainerRoleModal').modal('hide');
				}, (error: AxiosError) => {
					if (error && error.response && error.response.data && error.response.data.errorType === ErrorType.DuplicateEmailAddress) {
						this.isEmailIdAlreadyExist = true;
					}
				});
		},
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
	},
	created(): void {
		if (window.__PRERENDER_INJECTED || RegularExpressionConstant.authorRegex.test(window.location.href)) {
			return;
		}

		Promise.all([this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore()]).then(() => {
			if (!this.getIsEmployeeProfileValid) {
				window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.loginResource));
			} else {
				return this.validateUserRole();
			}
		}).finally(() => {
			this.initializing = false;
		});
	},
	validations: {
		trainerRoleForm: {
			trainerRole: {
				required,
			},
		},
	},
	watch: {
		employeeId() {
			if (!this.employeeId) {
				this.employeeSearchResponse = {} as EcCommon.IEmployeeSearchResponse;
				this.formSubmitted = false;
			}
		},
	},
});
